import type React from 'react';
import type { CSSProperties } from 'react';
import styles from './board-markers.module.scss';
import { StartRings } from './board-symbols';
import type { BoardHotLetter, CellCoord } from './board-types';

interface BoardMarkersProps {
  start?: CellCoord;
  hotLetters: BoardHotLetter[];
  /** Cells a tile covers, `row,col` — a marker under a tile is not drawn. */
  covered: Set<string>;
  cellSize: number;
  gap: number;
  padding: number;
}

/** The start square's accessible name. It states no multiplier because it carries none. */
export const START_MARKER_LABEL = 'Start square';

/** Shrinks a cluster's characters to fit one cell above the badge — two at 0.36 of it, three at 0.26. */
const letterScale = (letter: string): number => (letter.length <= 1 ? 0.44 : letter.length === 2 ? 0.36 : 0.26);

/**
 * The cell markers drawn over the grid: the start square, when a game places it, and hot letters.
 *
 * The grid underneath is hidden from assistive technology, so these are drawn as a layer of their own
 * with `role="img"` and a name each — "Start square", "Hot letter E, plus 10 points" — and a screen
 * reader moving across the board meets them among the tiles.
 */
export const BoardMarkers: React.FC<BoardMarkersProps> = ({ start, hotLetters, covered, cellSize, gap, padding }) => {
  const at = (cell: CellCoord) =>
    ({
      '--marker-left': `${padding + cell.col * (cellSize + gap)}px`,
      '--marker-top': `${padding + cell.row * (cellSize + gap)}px`,
      '--marker-size': `${cellSize}px`,
    }) as CSSProperties;
  const isCovered = (cell: CellCoord) => covered.has(`${cell.row},${cell.col}`);

  return (
    <>
      {start && !isCovered(start) && (
        <div
          role="img"
          aria-label={START_MARKER_LABEL}
          data-marker="start"
          className={`${styles.marker} ${styles.start}`}
          style={at(start)}
        >
          <StartRings />
        </div>
      )}
      {hotLetters
        .filter((hot) => !isCovered(hot))
        .map((hot) => (
          <div
            key={`${hot.row},${hot.col}`}
            role="img"
            aria-label={hot.label}
            data-marker="hot-letter"
            data-row={hot.row}
            data-col={hot.col}
            data-letter={hot.letter}
            className={`${styles.marker} ${hot.tone === 'other' ? styles.other : styles.hot}`}
            style={{ ...at(hot), '--letter-scale': letterScale(hot.letter) } as CSSProperties}
          >
            <span className={styles.letter} aria-hidden="true">
              {hot.letter}
            </span>
            <span className={styles.badge} aria-hidden="true">
              {hot.badge}
            </span>
          </div>
        ))}
    </>
  );
};
